import { useRef, useState } from "react";
import { Page } from "react-pdf";
import { Rnd } from "react-rnd";
import { X } from "lucide-react";
import { useSignature, FIELD_TYPES, fieldConfig } from "./SignatureContext";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function PdfRead({ pageNumber }) {
  const containerRef = useRef(null);
  const { blocks, setBlocks, activeFieldType } = useSignature();
  const [draft, setDraft] = useState(null);

  const pageBlocks = blocks.filter((b) => b.pageNumber === pageNumber);

  function getRelativePoint(e) {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  return (
    <div
      ref={containerRef}
      className="relative bg-white rounded-xl shadow-sm overflow-hidden cursor-crosshair"
      onMouseDown={(e) => {
        if (e.target.closest(".react-draggable")) return;
        const p = getRelativePoint(e);
        setDraft({
          startX: p.x,
          startY: p.y,
          x: p.x,
          y: p.y,
          width: 0,
          height: 0,
          fieldType: activeFieldType,
        });
      }}
      onMouseMove={(e) => {
        if (!draft) return;
        const p = getRelativePoint(e);
        setDraft({
          ...draft,
          x: Math.min(p.x, draft.startX),
          y: Math.min(p.y, draft.startY),
          width: Math.abs(p.x - draft.startX),
          height: Math.abs(p.y - draft.startY),
        });
      }}
      onMouseUp={() => {
        if (!draft || draft.width < 20 || draft.height < 20) {
          setDraft(null);
          return;
        }
        setBlocks((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            pageNumber,
            x: draft.x,
            y: draft.y,
            width: draft.width,
            height: draft.height,
            fieldType: draft.fieldType,
          },
        ]);
        setDraft(null);
      }}
    >
      <Page
        pageNumber={pageNumber}
        renderAnnotationLayer={false}
        renderTextLayer={false}
      />

      {/* Draft Block */}
      {draft && (
        <div
          className={cn(
            "absolute z-10 rounded-md border-2",
            fieldConfig[draft.fieldType]?.borderColor,
            fieldConfig[draft.fieldType]?.bgColor,
          )}
          style={{
            left: draft.x,
            top: draft.y,
            width: draft.width,
            height: draft.height,
          }}
        />
      )}

      {pageBlocks.map((block) => {
        const config =
          fieldConfig[block.fieldType] || fieldConfig[FIELD_TYPES.SIGNATURE];
        const Icon = config.icon;

        return (
          <Rnd
            key={block.id}
            bounds="parent"
            size={{ width: block.width, height: block.height }}
            position={{ x: block.x, y: block.y }}
            onDragStop={(e, d) =>
              setBlocks((prev) =>
                prev.map((b) =>
                  b.id === block.id ? { ...b, x: d.x, y: d.y } : b,
                ),
              )
            }
            onResizeStop={(e, dir, ref, delta, pos) =>
              setBlocks((prev) =>
                prev.map((b) =>
                  b.id === block.id
                    ? {
                        ...b,
                        width: ref.offsetWidth,
                        height: ref.offsetHeight,
                        x: pos.x,
                        y: pos.y,
                      }
                    : b,
                ),
              )
            }
            className={cn(
              "group relative z-20 rounded-lg transition-all",
              `border-2 ${config.borderColor} ${config.bgColor}`,
            )}
            style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
          >
            {/* Delete Button */}
            <button
              onClick={() =>
                setBlocks((prev) => prev.filter((b) => b.id !== block.id))
              }
              className="
                absolute -top-3 -right-3 z-30 hidden h-6 w-6
                items-center justify-center rounded-full
                border border-red-200 bg-white text-red-500
                shadow-sm hover:bg-red-50 group-hover:flex
              "
            >
              <X className="h-4 w-4" />
            </button>
            <div
              className={cn(
                "pointer-events-none absolute inset-0 flex items-center justify-center gap-1 rounded-md",
                config.tagBg,
              )}
            >
              <Icon size={14} className={config.textColor} />
              <span
                className={cn(
                  "select-none text-xs font-semibold",
                  config.textColor,
                )}
              >
                {config.label}
              </span>
            </div>
          </Rnd>
        );
      })}
    </div>
  );
}
